// src/theme/index.js
import { createTheme, alpha } from "@mui/material/styles";

const primary = { main: "#4F46E5" };   // indigo
const secondary = { main: "#06B6D4" }; // cyan
const success = { main: "#22c55e" };
const warning = { main: "#f59e0b" };
const error   = { main: "#ef4444" };

export default function createAppTheme(isDark = false) {
  const mode = isDark ? "dark" : "light";

  return createTheme({
    palette: {
      mode,
      primary,
      secondary,
      success,
      warning,
      error,
      background: {
        default: mode === "dark" ? "#0B1020" : "#F7F7FB",
        paper:    mode === "dark" ? "#121934" : "#FFFFFF",
      },
      divider: mode === "dark" ? alpha("#ffffff", 0.1) : "#EAEAF1",
      text: {
        primary: mode === "dark" ? "#F5F7FF" : "#0B1020",
        secondary: mode === "dark" ? alpha("#F5F7FF", 0.7) : "#5B6171",
      },
    },
    shape: { borderRadius: 12 },
    spacing: 8,
    typography: {
      fontFamily: ['Inter', 'Roboto', 'Helvetica', 'Arial', 'sans-serif'].join(','),
      h1: { fontWeight: 700, letterSpacing: -0.5 },
      h2: { fontWeight: 700, letterSpacing: -0.5 },
      h3: { fontWeight: 700, letterSpacing: -0.4 },
      h4: { fontWeight: 700, letterSpacing: -0.2 },
      h5: { fontWeight: 600 },
      h6: { fontWeight: 600 },
      button: { textTransform: "none", fontWeight: 600 },
    },
    components: {
      MuiCssBaseline: {
        styleOverrides: {
          body: { WebkitFontSmoothing: "antialiased", MozOsxFontSmoothing: "grayscale" },
          "*::-webkit-scrollbar": { width: 10, height: 10 },
          "*::-webkit-scrollbar-thumb": {
            backgroundColor: mode === "dark" ? alpha("#fff", 0.16) : "#D4D6DD",
            borderRadius: 999,
          },
        },
      },
      MuiPaper: {
        styleOverrides: {
          root: {
            borderRadius: 12,
            boxShadow:
              mode === "dark"
                ? "0 8px 24px rgba(0,0,0,0.45)"
                : "0 10px 30px rgba(15, 23, 42, 0.06)",
          },
        },
      },
      MuiButton: {
        defaultProps: { disableElevation: true },
        styleOverrides: {
          root: { borderRadius: 12, paddingTop: 10, paddingBottom: 10 },
          containedPrimary: {
            boxShadow: "0 8px 18px rgba(79,70,229,0.25)",
            ":hover": { boxShadow: "0 8px 20px rgba(79,70,229,0.35)" },
          },
          outlined: { borderColor: alpha("#0b1020", 0.12) },
        },
      },
      MuiTextField: {
        defaultProps: { size: "medium" },
        styleOverrides: {
          root: {
            "& .MuiOutlinedInput-root": {
              borderRadius: 12,
              background: mode === "dark" ? alpha("#fff", 0.03) : "#fff",
            },
          },
        },
      },
      MuiCard: {
        styleOverrides: {
          root: {
            borderRadius: 16,
            overflow: "hidden",
            border: mode === "dark" ? `1px solid ${alpha("#fff", 0.08)}` : "1px solid #F0F1F5",
          },
        },
      },
      MuiDialog: {
        styleOverrides: { paper: { borderRadius: 16 } },
      },
      MuiAppBar: {
        styleOverrides: {
          root: {
            background: mode === "dark" ? "#0D1530" : "#FFFFFF",
            color: "inherit",
            borderBottom: mode === "dark" ? `1px solid ${alpha("#fff",0.06)}` : "1px solid #ECECF2",
          },
        },
      },
      MuiDrawer: {
        styleOverrides: {
          paper: {
            borderRight: mode === "dark" ? `1px solid ${alpha("#fff",0.06)}` : "1px solid #ECECF2",
          },
        },
      },
      MuiListItemButton: {
        styleOverrides: {
          root: {
            borderRadius: 10,
            marginBottom: 4,
            "&.Mui-selected": {
              background:
                mode === "dark" ? alpha(primary.main, 0.18) : alpha(primary.main, 0.10),
              "& .MuiListItemIcon-root, & .MuiListItemText-primary": { color: primary.main },
            },
          },
        },
      },
      MuiTooltip: {
        styleOverrides: {
          tooltip: { borderRadius: 8, fontSize: 12, padding: "8px 10px" },
        },
      },
    },
  });
}
